import type { OpsSupportRequestsResponse } from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { listSupportRequests } from './supportRequests.ts';
import type { OpsSupportRequestsQuery } from './supportRequests.ts';

/**
 * React Query hook for `/v1/ops/support/requests`.
 *
 * Keying includes the full query tuple so distinct filter combinations
 * don't alias in the cache. The query is disabled until `accountId` is
 * non-empty; the support page needs an explicit tenant selection before
 * it starts fetching, so the empty initial state never issues a request.
 */

export const SUPPORT_REQUESTS_ROOT_KEY = ['support', 'requests'] as const;

export function supportRequestsQueryKey(
  query: OpsSupportRequestsQuery,
): readonly ['support', 'requests', OpsSupportRequestsQuery] {
  return ['support', 'requests', query] as const;
}

export function useSupportRequests(
  query: OpsSupportRequestsQuery,
): UseQueryResult<OpsSupportRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: supportRequestsQueryKey(query),
    queryFn: ({ signal }) => listSupportRequests(query, fetchImpl, signal),
    enabled: query.accountId.trim().length > 0,
  });
}
